import { jsx, jsxs } from 'react/jsx-runtime';
import { Button as Button$1, Box, Typography, IconButton, Dialog } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TourProvider, useTour } from '@reactour/tour';
export { TourProvider } from '@reactour/tour';
import { Box as Box$1 } from '@mui/system';

const Button = ({ onClick, children, variant, disabled, }) => {
    return (jsx(Button$1, { disabled: disabled, size: "medium", variant: "contained", sx: {
            padding: "16px 32px",
            borderRadius: "4px",
            backgroundColor: variant === "dark" ? "#222733" : "#fff",
            color: variant == "dark" ? "#fff" : "#000",
            textTransform: "none",
            boxShadow: "none",
            fontSize: "14px",
            height: "36px",
            transition: "background-color 0.2s, color 0.2s",
            "&:hover": {
                boxShadow: "none",
            },
        }, onClick: onClick, children: children }));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const DEFAULT_TUTORIAL_LABELS = {
    en: {
        welcomeScreen: {
            closeButtonAriaLabel: "Close tutorial welcome dialog",
            logoAltText: "Pimo Logo",
            skipButtonLabel: "Skip",
            startButtonLabel: "Start tutorial",
        },
        stepContent: {
            closeButtonAriaLabel: "Close tutorial",
            backButtonLabel: "Back",
            nextButtonLabel: "Next",
            finishButtonLabel: "Close",
        },
    },
    de: {
        welcomeScreen: {
            closeButtonAriaLabel: "Tutorial-Begrüßung schliessen",
            logoAltText: "Pimo-Logo",
            skipButtonLabel: "Überspringen",
            startButtonLabel: "Tutorial starten",
        },
        stepContent: {
            closeButtonAriaLabel: "Tutorial schließen",
            backButtonLabel: "Zurück",
            nextButtonLabel: "Weiter",
            finishButtonLabel: "Schließen",
        },
    },
};
function normalizeTutorialLanguage(language) {
    return language === "de" ? "de" : "en";
}
function resolveTutorialLabels(labels, language) {
    const defaults = DEFAULT_TUTORIAL_LABELS[normalizeTutorialLanguage(language)];
    return {
        welcomeScreen: Object.assign(Object.assign({}, defaults.welcomeScreen), labels === null || labels === void 0 ? void 0 : labels.welcomeScreen),
        stepContent: Object.assign(Object.assign({}, defaults.stepContent), labels === null || labels === void 0 ? void 0 : labels.stepContent),
    };
}

function createLocalizedText(en, de) {
    return { en, de };
}
function getLocalizedText(copy, language) {
    var _a;
    if (!copy) {
        return "";
    }
    const resolvedLanguage = normalizeTutorialLanguage(language);
    return (_a = copy[resolvedLanguage]) !== null && _a !== void 0 ? _a : copy.en;
}

const DEFAULT_PROJECT_ENTITY_NAME = {
    en: { singular: "Project", plural: "Projects" },
    de: {
        singular: "Projekt",
        plural: "Projekte",
        gender: "neuter",
        compoundStem: "Projekt",
    },
};
const DEFAULT_ITEM_ENTITY_NAME = {
    en: { singular: "Deliverable", plural: "Deliverables" },
    de: {
        singular: "Lieferergebnis",
        plural: "Lieferergebnisse",
        gender: "neuter",
        compoundStem: "Lieferergebnis",
    },
};
function resolveEntityNames(names) {
    var _a, _b;
    return {
        project: (_a = names === null || names === void 0 ? void 0 : names.project) !== null && _a !== void 0 ? _a : DEFAULT_PROJECT_ENTITY_NAME,
        item: (_b = names === null || names === void 0 ? void 0 : names.item) !== null && _b !== void 0 ? _b : DEFAULT_ITEM_ENTITY_NAME,
    };
}
/** Returns the compound stem, defaulting to the singular form. */
function getDeCompoundStem(de) {
    var _a;
    return (_a = de.compoundStem) !== null && _a !== void 0 ? _a : de.singular;
}
/**
 * Returns the German indefinite article "jede(r/s)" in accusative for use in
 * "für jede(n/s) X" phrases ("für jedes Projekt", "für jeden Befund", "für jede Initiative").
 */
function getDeForEachArticleAccusative(gender) {
    switch (gender) {
        case "masculine":
            return "jeden";
        case "feminine":
            return "jede";
        case "neuter":
            return "jedes";
    }
}
/**
 * Returns the German indefinite article "jede(r/s)" in nominative for use in
 * "jede(r/s) X hat …" phrases ("jedes Projekt hat", "jeder Befund hat", "jede Initiative hat").
 */
function getDeForEachArticleNominative(gender) {
    switch (gender) {
        case "masculine":
            return "jeder";
        case "feminine":
            return "jede";
        case "neuter":
            return "jedes";
    }
}
/**
 * Returns the German accusative form of "ein" used in
 * "auf eine(n/s) der X" phrases.
 */
function getDeAccusativePartitiveArticle(gender) {
    switch (gender) {
        case "masculine":
            return "einen";
        case "feminine":
            return "eine";
        case "neuter":
            return "eines";
    }
}
/**
 * Returns the German dative demonstrative article used in
 * "zu diese(m/r) X" phrases.
 */
function getDeDativeDemonstrativeArticle(gender) {
    return gender === "feminine" ? "dieser" : "diesem";
}

function normalizeRoutePath(path) {
    const trimmed = path.trim();
    if (!trimmed) {
        return "/";
    }
    try {
        const url = new URL(trimmed, window.location.origin);
        const normalizedPathname = url.pathname.replace(/\/+$/, "") || "/";
        return `${normalizedPathname}${url.search}${url.hash}`;
    }
    catch (_a) {
        const normalized = trimmed.replace(/\/+$/, "") || "/";
        return normalized.startsWith("/") ? normalized : `/${normalized}`;
    }
}

function NavigationDots({ count, current, }) {
    return (jsx("ul", { style: { display: "flex", flexDirection: "row", gap: "8px", padding: 0 }, children: Array.from({ length: count }).map((_, index) => {
            return (jsx("li", { style: {
                    listStyle: "none",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: current === index ? "black" : "lightgrey",
                } }, `tutorial-navigation-dot-${index}`));
        }) }));
}

const SELECTOR_POLL_INTERVAL_MS = 50;
const SELECTOR_POLL_TIMEOUT_MS = 5000;
/**
 * Navigates to `targetPath` (if not already there), then waits until
 * `selector` is present in the DOM before resolving.
 * Rejects after {@link SELECTOR_POLL_TIMEOUT_MS} ms to avoid hanging forever.
 */
function navigateAndWaitForSelector(navigate, targetPath, selector) {
    return new Promise((resolve) => {
        const normalizedTarget = normalizeRoutePath(targetPath);
        const currentPath = normalizeRoutePath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
        if (currentPath !== normalizedTarget) {
            navigate(normalizedTarget);
        }
        const deadline = Date.now() + SELECTOR_POLL_TIMEOUT_MS;
        const poll = () => {
            if (document.querySelector(selector)) {
                resolve();
                return;
            }
            if (Date.now() > deadline) {
                // Timed out — resolve anyway so the tour still advances.
                resolve();
                return;
            }
            setTimeout(poll, SELECTOR_POLL_INTERVAL_MS);
        };
        poll();
    });
}
function ContentComponent(_a) {
    var { setIsOpen, setCurrentStep, steps, setTutorialCompleted, labels, language } = _a, props = __rest(_a, ["setIsOpen", "setCurrentStep", "steps", "setTutorialCompleted", "labels", "language"]);
    const isFirstStep = props.currentStep === 0;
    const isLastStep = props.currentStep === steps.length - 1;
    const currentStep = steps[props.currentStep];
    const content = currentStep.content;
    const navigate = useNavigate();
    const resolvedLabels = resolveTutorialLabels(labels, language);
    // When navigating to the next/previous step's route we hide the popover so
    // it doesn't flash in the top-left corner while the page is loading.
    const [isNavigating, setIsNavigating] = useState(false);
    const navigatingRef = useRef(false);
    // Handle navigation for the initial step when the tour first opens, covering
    // the case where the starting step has a navigateTo target.
    const initialStepHandled = useRef(false);
    useEffect(() => {
        if (initialStepHandled.current) {
            return;
        }
        initialStepHandled.current = true;
        const targetPath = currentStep.navigateTo;
        if (!targetPath) {
            return;
        }
        const normalizedTarget = normalizeRoutePath(targetPath);
        const currentPath = normalizeRoutePath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
        if (currentPath !== normalizedTarget) {
            navigate(normalizedTarget);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const goToStep = useCallback((nextIndex) => __awaiter(this, void 0, void 0, function* () {
        if (navigatingRef.current) {
            return;
        }
        const nextStep = steps[nextIndex];
        const targetPath = nextStep === null || nextStep === void 0 ? void 0 : nextStep.navigateTo;
        if (targetPath && nextStep.selector) {
            navigatingRef.current = true;
            setIsNavigating(true);
            try {
                yield navigateAndWaitForSelector(navigate, targetPath, nextStep.selector);
            }
            finally {
                navigatingRef.current = false;
                setIsNavigating(false);
            }
        }
        setCurrentStep(() => nextIndex);
    }), [navigate, setCurrentStep, steps]);
    const handleClose = () => {
        setIsOpen(false);
        setTutorialCompleted === null || setTutorialCompleted === void 0 ? void 0 : setTutorialCompleted();
    };
    return (jsxs(Box, { sx: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            visibility: isNavigating ? "hidden" : "visible",
        }, children: [jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "start",
                }, children: [jsx(Typography, { variant: "h6", marginBottom: "16px", children: currentStep.title }), jsx(IconButton, { sx: { padding: 0 }, onClick: handleClose, "aria-label": resolvedLabels.stepContent.closeButtonAriaLabel, children: jsx(Close, {}) })] }), jsx(Typography, { children: content }), jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginTop: "32px",
                }, children: [jsx(NavigationDots, { count: steps.length, current: props.currentStep }), jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [!isFirstStep && (jsx(Button, { variant: "white", onClick: () => goToStep(props.currentStep - 1), disabled: isNavigating, children: resolvedLabels.stepContent.backButtonLabel })), jsx(Button, { variant: "dark", onClick: () => {
                                    if (isLastStep) {
                                        handleClose();
                                    }
                                    else {
                                        void goToStep(props.currentStep + 1);
                                    }
                                }, disabled: isNavigating, children: isLastStep
                                    ? resolvedLabels.stepContent.finishButtonLabel
                                    : resolvedLabels.stepContent.nextButtonLabel })] })] })] }));
}

const tourStyles = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    popover: (base, state) => {
        return Object.assign(Object.assign(Object.assign({}, base), { borderRadius: 10, maxWidth: "600px" }), doArrow(state.position, state.verticalAlign, state.horizontalAlign));
    },
};
const oppositeSide = {
    top: "bottom",
    bottom: "top",
    right: "left",
    left: "right",
};
const popoverPadding = 10;
function doArrow(position, verticalAlign, horizontalAlign) {
    if (!position || position === "custom") {
        return {};
    }
    const width = 16;
    const height = 12;
    const color = "white";
    const isVertical = position === "top" || position === "bottom";
    const spaceFromSide = 10;
    const obj = {
        [`--rtp-arrow-${isVertical ? oppositeSide[horizontalAlign] : verticalAlign}`]: height + spaceFromSide + "px",
        [`--rtp-arrow-${oppositeSide[position]}`]: -height + 2 + "px",
        [`--rtp-arrow-border-${isVertical ? "left" : "top"}`]: `${width / 2}px solid transparent`,
        [`--rtp-arrow-border-${isVertical ? "right" : "bottom"}`]: `${width / 2}px solid transparent`,
        [`--rtp-arrow-border-${position}`]: `${height}px solid ${color}`,
    };
    return obj;
}

function TutorialProvider(props) {
    return jsx(TourProvider, Object.assign({ styles: tourStyles }, props));
}

function useSafeTour() {
    try {
        return useTour();
    }
    catch (_a) {
        // Guard against provider/context mismatches so the welcome modal still renders.
        return {
            setIsOpen: undefined,
            setSteps: undefined,
        };
    }
}
const TutorialScreen = ({ logo, text, setTutorialCompleted, stateSteps, language = "en", labels, }) => {
    const [welcomeModalIsOpen, setWelcomeModalIsOpen] = useState(true);
    const { setIsOpen, setSteps } = useSafeTour();
    const resolvedLabels = resolveTutorialLabels(labels, language);
    useEffect(() => stateSteps && (setSteps === null || setSteps === void 0 ? void 0 : setSteps(stateSteps)), [setSteps, stateSteps]);
    return (jsxs(Dialog, { "data-testid": "tutorial-welcome-dialog", open: welcomeModalIsOpen, onClose: (_, reason) => {
            if (reason === "backdropClick") {
                return;
            }
            setWelcomeModalIsOpen(false);
        }, title: "", slotProps: {
            paper: {
                sx: {
                    borderRadius: 4,
                },
            },
        }, children: [jsx(Box$1, { sx: {
                    display: "flex",
                    flexDirection: "row",
                    margin: "16px 16px 0 16px",
                    justifyContent: "end",
                    height: "24px",
                }, children: jsx(IconButton, { onClick: () => {
                        setWelcomeModalIsOpen(false);
                        setTutorialCompleted();
                    }, "aria-label": resolvedLabels.welcomeScreen.closeButtonAriaLabel, children: jsx(Close, {}) }) }), jsxs(Box$1, { sx: {
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    paddingX: "64px",
                }, children: [jsx(Box$1, { component: "img", sx: {
                            maxWidth: 200,
                            minWidth: 200,
                            content: {
                                xs: `url(${logo})`, //img src from xs up to sm <600
                            },
                        }, alt: resolvedLabels.welcomeScreen.logoAltText }), jsx(Typography, { lineHeight: "32px", fontWeight: "500", marginY: "48px", children: text }), jsxs(Box$1, { sx: {
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "end",
                            width: "100%",
                            gap: "16px",
                            marginY: "32px",
                        }, children: [jsx(Button, { variant: "white", onClick: () => {
                                    setTutorialCompleted();
                                    setWelcomeModalIsOpen(false);
                                }, children: resolvedLabels.welcomeScreen.skipButtonLabel }), jsx(Button, { variant: "dark", onClick: () => {
                                    setWelcomeModalIsOpen(false);
                                    setIsOpen === null || setIsOpen === void 0 ? void 0 : setIsOpen(true);
                                }, children: resolvedLabels.welcomeScreen.startButtonLabel })] })] })] }));
};

function buildDashboardDescription(p) {
    return createLocalizedText(`In the dashboard, you can see all important data about your program across all ${p.en.plural}. This page is only accessible to the program team and the administrators.`, `Im Dashboard sehen Sie alle wichtigen Daten zu Ihrem Programm über alle ${p.de.plural} hinweg. Diese Seite ist nur für das Programmteam und Administratoren zugänglich.`);
}
function buildProjectReportsAdminDescription(p, i) {
    const deForEachAcc = `${getDeForEachArticleAccusative(p.de.gender)} ${p.de.singular}`;
    const deForEachNom = `${getDeForEachArticleNominative(p.de.gender)} ${p.de.singular}`;
    const deCompound = getDeCompoundStem(p.de);
    return createLocalizedText(`There is a reporting page for each ${p.en.singular}. By default, each ${p.en.singular} has a ${p.en.singular} dashboard and a page with a list of detailed ${i.en.plural}. Administrators and the program team have access to all ${p.en.plural}. ${p.en.singular} teams only have access to the ${p.en.plural} assigned to them.`, `Für ${deForEachAcc} gibt es eine Reporting-Seite. Standardmäßig hat ${deForEachNom} ein ${deCompound}-Dashboard und eine Seite mit einer Liste detaillierter ${i.de.plural}. Administratoren und das Programmteam haben Zugriff auf alle ${p.de.plural}. ${deCompound}teams haben nur Zugriff auf die ihnen zugewiesenen ${p.de.plural}.`);
}
function buildProjectReportsProjectDescription(p) {
    const deAccPartitive = getDeAccusativePartitiveArticle(p.de.gender);
    const deDativeDemonstrative = getDeDativeDemonstrativeArticle(p.de.gender);
    return createLocalizedText(`The navigation bar displays all ${p.en.plural} that have been assigned to you as a user. If you click on one of the ${p.en.plural}, you can view all the details about that ${p.en.singular}.`, `Die Navigationsleiste zeigt alle ${p.de.plural} an, die Ihnen als Benutzer zugewiesen wurden. Wenn Sie auf ${deAccPartitive} der ${p.de.plural} klicken, können Sie alle Details zu ${deDativeDemonstrative} ${p.de.singular} ansehen.`);
}
function buildAddProjectsDescription(p) {
    return createLocalizedText(`You can create new ${p.en.plural} here. This function is available to the program team and administrators. Alternatively, you can create ${p.en.plural} in the Reporting Settings.`, `Hier können Sie neue ${p.de.plural} anlegen. Diese Funktion steht dem Programmteam und Administratoren zur Verfügung. Alternativ können Sie ${p.de.plural} in den Reporting-Einstellungen anlegen.`);
}
function buildUpdateReportAdminDescription(p) {
    const deCompound = getDeCompoundStem(p.de);
    return createLocalizedText(`Reports can easily be created and updated here. This is the task of the ${p.en.singular} team. People with view rights do not see this function. When the report is saved, the update status is updated accordingly and made visible with the smiley system.`, `Berichte können hier einfach erstellt und aktualisiert werden. Das ist die Aufgabe des ${deCompound}teams. Personen mit reinen Leserechten sehen diese Funktion nicht. Wenn der Bericht gespeichert wird, wird der Aktualisierungsstatus entsprechend aktualisiert und über das Smiley-System sichtbar gemacht.`);
}
function buildUpdateReportProjectDescription(p) {
    const deCompound = getDeCompoundStem(p.de);
    return createLocalizedText(`If you have the appropriate permissions, you can update your ${p.en.singular} report here. When the report is saved, the update status is updated accordingly and made visible with the smiley system.`, `Wenn Sie über die entsprechenden Berechtigungen verfügen, können Sie Ihren ${deCompound}bericht hier aktualisieren. Wenn der Bericht gespeichert wird, wird der Aktualisierungsstatus entsprechend aktualisiert und über das Smiley-System sichtbar gemacht.`);
}
const actionBarAdminDescription = createLocalizedText("Here you can view the following information: Status update overview, notifications and the change log. The program team and administrators can view information about all projects. Project teams can only view data for the projects assigned to them.", "Hier können Sie folgende Informationen einsehen: Statusübersicht, Benachrichtigungen und das Änderungsprotokoll. Das Programmteam und Administratoren können Informationen zu allen Projekten sehen. Projektteams können nur Daten der ihnen zugewiesenen Projekte sehen.");
const actionBarProjectDescription = createLocalizedText("Here you can view the following information: Status update overview of your projects, notifications and the change log", "Hier können Sie folgende Informationen einsehen: Statusübersicht Ihrer Projekte, Benachrichtigungen und das Änderungsprotokoll");
function buildDefaultSteps(entityNames) {
    const p = entityNames.project;
    const i = entityNames.item;
    const deCompound = getDeCompoundStem(p.de);
    return [
        {
            title: createLocalizedText("Dashboard", "Dashboard"),
            selector: "[data-tourid=dashboard]",
            contents: {
                admin: buildDashboardDescription(p),
                program: buildDashboardDescription(p),
            },
            navigateTo: {
                admin: "/dashboard",
                program: "/dashboard",
                project: "/dashboard",
            },
            position: "right",
            appliesFor: ["admin", "program"],
        },
        {
            title: createLocalizedText(`${p.en.singular} Reports`, `${deCompound}berichte`),
            selector: "[data-tourid=project-reports]",
            contents: {
                admin: buildProjectReportsAdminDescription(p, i),
                project: buildProjectReportsProjectDescription(p),
                program: buildProjectReportsAdminDescription(p, i),
            },
            position: "right",
            appliesFor: ["admin", "project", "program"],
        },
        {
            title: createLocalizedText(`Add new ${p.en.plural}`, `Neue ${p.de.plural} anlegen`),
            selector: "[data-tourid=new-project]",
            contents: {
                admin: buildAddProjectsDescription(p),
                program: buildAddProjectsDescription(p),
            },
            position: "right",
            appliesFor: ["admin", "program"],
        },
        {
            title: createLocalizedText(`Update ${p.en.singular} Report`, `${deCompound}bericht aktualisieren`),
            selector: "[data-tourid=update-report]",
            contents: {
                admin: buildUpdateReportAdminDescription(p),
                project: buildUpdateReportProjectDescription(p),
                program: buildUpdateReportAdminDescription(p),
            },
            navigateTo: {
                admin: "/reports/{id}/dashboard",
                program: "/reports/{id}/dashboard",
                project: "/reports/{id}/dashboard",
            },
            position: "bottom",
            appliesFor: ["admin", "project", "program"],
        },
        {
            title: createLocalizedText("Action Bar", "Aktionsleiste"),
            selector: "[data-tourid=action-bar]",
            contents: {
                admin: actionBarAdminDescription,
                project: actionBarProjectDescription,
                program: actionBarAdminDescription,
            },
            position: "bottom",
            appliesFor: ["admin", "project", "program"],
        },
    ];
}
function resolveDefaultStepArguments(languageOrReplaceTokens, entityNamesOrReplaceTokens, replaceTokens) {
    // Legacy signature: getDefaultStepsForRole(role, replaceTokens)
    if (Array.isArray(languageOrReplaceTokens)) {
        return {
            language: undefined,
            entityNames: undefined,
            replaceTokens: languageOrReplaceTokens,
        };
    }
    // New signature: getDefaultStepsForRole(role, language, entityNames, replaceTokens)
    if (entityNamesOrReplaceTokens != null &&
        !Array.isArray(entityNamesOrReplaceTokens)) {
        return {
            language: languageOrReplaceTokens,
            entityNames: entityNamesOrReplaceTokens,
            replaceTokens,
        };
    }
    // Legacy signature: getDefaultStepsForRole(role, language, replaceTokens)
    return {
        language: languageOrReplaceTokens,
        entityNames: undefined,
        replaceTokens: entityNamesOrReplaceTokens,
    };
}
function getDefaultStepsForRole(role, languageOrReplaceTokens, entityNamesOrReplaceTokens, replaceTokens) {
    const resolved = resolveDefaultStepArguments(languageOrReplaceTokens, entityNamesOrReplaceTokens, replaceTokens);
    const steps = buildDefaultSteps(resolveEntityNames(resolved.entityNames));
    return steps
        .filter((step) => step.appliesFor.includes(role))
        .map((_a) => {
        var _b, _c;
        var { title, contents, navigateTo } = _a, step = __rest(_a, ["title", "contents", "navigateTo"]);
        return (Object.assign(Object.assign({}, step), { title: getLocalizedText(title, resolved.language), content: getLocalizedText(contents[role], resolved.language), navigateTo: (_b = resolved.replaceTokens) === null || _b === void 0 ? void 0 : _b.reduce((acc, { replaceToken, value }) => acc.replace(replaceToken, value), (_c = navigateTo === null || navigateTo === void 0 ? void 0 : navigateTo[role]) !== null && _c !== void 0 ? _c : "") }));
    });
}

export { Button, ContentComponent, NavigationDots, TutorialProvider, TutorialScreen, getDefaultStepsForRole, normalizeTutorialLanguage, popoverPadding, resolveTutorialLabels, tourStyles };
